import mixpanel from 'mixpanel-browser';
import i18n from '@/utils/i18n';
import { EVENTS } from '@/utils/analyticsEvents';

type TrackProperties = Record<string, unknown>;
type EntrySource = 'sidebar' | 'dashboard' | 'history';

type UserLike = {
  id?: number;
  auth_provider?: string;
  created_at?: string;
  last_active_at?: string | null;
};

type ProfileLike = {
  subscription?: {
    plan?: string;
    billing_period?: string;
  };
  profile_cv?: unknown;
  preferences?: {
    language?: string;
  };
};

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN;

const SESSION_START_KEY = 'mixpanel_session_start';
const SESSION_COUNT_KEY = 'mixpanel_session_count';
const LAST_SESSION_END_KEY = 'mixpanel_last_session_end';
const SESSION_PAGES_VIEWED_KEY = 'mixpanel_session_pages_viewed';
const SESSION_FEATURES_KEY = 'mixpanel_session_features_used';

const DAY_MS = 24 * 60 * 60 * 1000;

let initialized = false;
let sessionStarted = false;
let sessionEnded = false;
let pagesViewed = Number(localStorage.getItem(SESSION_PAGES_VIEWED_KEY) || 0);
let featuresUsed = new Set<string>(JSON.parse(localStorage.getItem(SESSION_FEATURES_KEY) || '[]'));

const readNumber = (key: string): number => {
  const value = Number(localStorage.getItem(key));
  return Number.isFinite(value) ? value : 0;
};

const ensureAnalytics = (): boolean => {
  if (!MIXPANEL_TOKEN) return false;
  if (!initialized) {
    mixpanel.init(MIXPANEL_TOKEN, {
      debug: import.meta.env.DEV,
      ignore_dnt: true,
      persistence: 'localStorage',
      api_host: 'https://api-eu.mixpanel.com',
    });
    initialized = true;
  }
  return true;
};

export const initAnalytics = (): void => {
  ensureAnalytics();
};

export const trackEvent = (eventName: string, properties: TrackProperties = {}): void => {
  if (!ensureAnalytics()) return;
  mixpanel.track(eventName, properties);
};

export const identifyUser = (userId?: string | number | null): void => {
  if (!ensureAnalytics() || !userId) return;
  mixpanel.identify(String(userId));
};

export const setUserProperties = (properties: TrackProperties): void => {
  if (!ensureAnalytics()) return;
  mixpanel.people.set(properties);
  mixpanel.register(properties);
};

export const setUserPropertiesOnce = (properties: TrackProperties): void => {
  if (!ensureAnalytics()) return;
  mixpanel.people.set_once(properties);
};

export const resetAnalytics = (): void => {
  if (!ensureAnalytics()) return;
  mixpanel.reset();
};

export const recordPageView = (path: string): void => {
  pagesViewed += 1;
  localStorage.setItem(SESSION_PAGES_VIEWED_KEY, String(pagesViewed));
  if (path && ensureAnalytics()) {
    mixpanel.register({ page: path });
  }
};

export const markFeatureUsed = (feature: string): void => {
  if (!feature) return;
  featuresUsed.add(feature);
  localStorage.setItem(SESSION_FEATURES_KEY, JSON.stringify([...featuresUsed]));
};

export const startSession = (properties: TrackProperties = {}): void => {
  if (sessionStarted) return;
  sessionStarted = true;

  const now = Date.now();
  const nextSessionNumber = readNumber(SESSION_COUNT_KEY) + 1;
  const lastSessionEnd = readNumber(LAST_SESSION_END_KEY);
  const daysSinceLastSession = lastSessionEnd ? Math.floor((now - lastSessionEnd) / DAY_MS) : undefined;

  localStorage.setItem(SESSION_START_KEY, String(now));
  localStorage.setItem(SESSION_COUNT_KEY, String(nextSessionNumber));
  localStorage.setItem(SESSION_PAGES_VIEWED_KEY, '0');
  localStorage.setItem(SESSION_FEATURES_KEY, '[]');
  pagesViewed = 0;
  featuresUsed = new Set();

  trackEvent(EVENTS.SESSION_STARTED, {
    preferred_language: i18n.language,
    days_since_last_session: daysSinceLastSession,
    session_number: nextSessionNumber,
    ...properties,
  });
};

export const endSession = (properties: TrackProperties = {}): void => {
  if (!sessionStarted || sessionEnded) return;
  sessionEnded = true;
  const now = Date.now();
  const sessionStart = readNumber(SESSION_START_KEY);
  const durationSeconds = sessionStart ? Math.max(0, Math.floor((now - sessionStart) / 1000)) : undefined;

  localStorage.setItem(LAST_SESSION_END_KEY, String(now));

  trackEvent(EVENTS.SESSION_ENDED, {
    session_duration_seconds: durationSeconds,
    features_used: [...featuresUsed],
    pages_viewed: pagesViewed,
    ...properties,
  });
};

export const getSessionDurationSeconds = (): number | undefined => {
  const sessionStart = readNumber(SESSION_START_KEY);
  if (!sessionStart) return undefined;
  return Math.max(0, Math.floor((Date.now() - sessionStart) / 1000));
};

export const computeDaysSince = (dateValue?: string | null): number | undefined => {
  if (!dateValue) return undefined;
  const timestamp = Date.parse(dateValue);
  if (!Number.isFinite(timestamp)) return undefined;
  return Math.floor((Date.now() - timestamp) / DAY_MS);
};

const mapSignupMethod = (provider?: string): string | undefined => {
  if (!provider) return undefined;
  if (provider.toLowerCase().includes('google')) return 'google';
  return 'email';
};

export const syncUserProfile = (user?: UserLike | null, profile?: ProfileLike | null): void => {
  if (!user?.id) return;
  identifyUser(user.id);
  setUserProperties({
    user_id: user.id,
    plan: profile?.subscription?.plan,
    billing_cycle: profile?.subscription?.billing_period,
    has_profile_cv: Boolean(profile?.profile_cv),
    preferred_language: profile?.preferences?.language || i18n.language,
    last_active_date: user.last_active_at || undefined,
  });
  setUserPropertiesOnce({
    signup_date: user.created_at,
    signup_method: mapSignupMethod(user.auth_provider),
  });
};

export const getUrlDomain = (url: string): string => {
  try {
    return new URL(url).hostname;
  } catch {
    return '';
  }
};

export const getFileExtension = (fileName: string): string => {
  const parts = fileName.split('.');
  if (parts.length < 2) return '';
  return parts[parts.length - 1]?.toLowerCase() || '';
};

export const getFileSizeKb = (file: File): number => Math.ceil(file.size / 1024);

export const normalizeEntrySource = (source?: string | null): EntrySource => {
  if (source === 'dashboard' || source === 'history' || source === 'sidebar') {
    return source;
  }
  return 'sidebar';
};

export const trackInitialPageLoadSlow = (page: string): void => {
  if (typeof performance === 'undefined' || !performance.getEntriesByType) return;
  const entry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  if (!entry) return;
  const loadTimeMs = entry.domContentLoadedEventEnd || entry.loadEventEnd;
  if (loadTimeMs > 3000) {
    trackEvent(EVENTS.PAGE_LOAD_SLOW, {
      page,
      load_time_ms: Math.round(loadTimeMs),
    });
  }
};
